import moment, { Moment } from 'moment';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { OPTIMIZATION_TYPE, Run, Task, User } from '@bringg/types';
import {
	OptimizationResponseFailed,
	RouteOptimizationResult
} from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import { isOneTimeFrameBetweenOtherTimeFrame } from '@bringg-frontend/utils';
import { DateRangeType } from '@bringg/dashboard-sdk/dist/DeliveryBlocks/DeliveryBlocks.consts';

import { executeAction } from 'bringg-web/services/cross-application/cross-application';
import useStores from 'bringg-web/recipes/use-stores';
import { ResourceWithBlockData } from 'bringg-web/features/optimization-wizard/types';
import getTeamId from '../run-assigner-team-id-provider';
import { ViewNames } from './consts';
import { ModalAction } from 'bringg-web/services/cross-application/cross-application.actions';
import { getOptimizationTimeFrameFromSelectedTasks } from 'bringg-web/features/optimization-wizard/optimization-time-frame-provider';
import { buildResourceWithBlockData } from 'bringg-web/features/optimization-wizard/optimization-resources-provider';
import {
	applyOptimizationResults,
	getOptimizationRequest
} from 'bringg-web/features/optimization-wizard/optimization-requests-provider';
import useUserAssignerStore from './use-user-assigner-store';

const useOptimizationFunctions = (source: { run?: Run; tasks?: Task[] }) => {
	const { authStore } = useStores();
	const {
		setOptimizationProgressPercentage,
		setOptimizationResult,
		selectedDriver,
		optimizationResult,
		setSentToOptimizationCount,
		updateOptimizationResults
	} = useUserAssignerStore();

	const teamId = getTeamId(source);

	const getResourcesForOptimization = async (
		tasksDateRange: { startTime: Date; endTime: Date },
		selectedDriver: User
	) => {
		const deliveryBlocks = await getRootEnv().dashboardSdk.sdk.deliveryBlocks.getByDriverId(
			selectedDriver.id,
			moment(tasksDateRange.startTime).unix(),
			moment(tasksDateRange.endTime).unix(),
			teamId,
			DateRangeType.overlapsWithRange
		);

		const selectedDriverResources: ResourceWithBlockData[] = [];
		deliveryBlocks.forEach(deliveryBlock =>
			deliveryBlock.delivery_block_resources.forEach(resource => {
				if (resource.user_id === selectedDriver.id) {
					selectedDriverResources.push(buildResourceWithBlockData(deliveryBlock, resource));
				}
			})
		);
		return selectedDriverResources;
	};

	const getTasksInOptimizationTimeFrame = (optimizationTimeRange: Moment[]) => {
		return source.tasks.filter(task => {
			const taskStartTime =
				task.way_points[0].no_earlier_than || task.way_points[0].first_attempt_promise_no_earlier_than;
			const taskEndTime =
				task.way_points[0].no_later_than || task.way_points[0].first_attempt_promise_no_later_than;

			return taskStartTime && taskEndTime
				? isOneTimeFrameBetweenOtherTimeFrame(
						[moment(taskStartTime), moment(taskEndTime)],
						optimizationTimeRange
				  )
				: true;
		});
	};

	const startOptimization = async (setCurrentView: (view: ViewNames) => void) => {
		const tasksDateRange = getOptimizationTimeFrameFromSelectedTasks(source.tasks);
		const resources = await getResourcesForOptimization(tasksDateRange, selectedDriver);
		const optimizationTimeRange = [moment(tasksDateRange.startTime), moment(tasksDateRange.endTime)];
		const tasksInTimeRange = getTasksInOptimizationTimeFrame(optimizationTimeRange);
		setSentToOptimizationCount({ tasks: tasksInTimeRange.length, resources: resources.length });
		const tasksIds = tasksInTimeRange.map(task => task.id);

		const request = getOptimizationRequest({
			teamId,
			selectedResourcesIds: resources.map(resource => resource.id),
			onOptimizationProgressUpdate: setOptimizationProgressPercentage,
			onOptimizationDone: (routeOptimizationResult: RouteOptimizationResult | OptimizationResponseFailed) =>
				setOptimizationResult(routeOptimizationResult),
			isOptimizeNewAndSaveExisting: false,
			tasksIds,
			startTime: tasksDateRange.startTime.toISOString(),
			endTime: tasksDateRange.endTime.toISOString(),
			resourcesList: resources
		});
		setCurrentView('optimizationView');
		await request;
	};

	const onApplyOptimizationResult = async (onCloseModal: () => void) => {
		await applyOptimizationResults([optimizationResult]);
		onCloseModal();
	};

	const applyRoutesPlannerChanges = optimizationResult => {
		updateOptimizationResults(optimizationResult.data);
	};

	const openRoutesPlanner = () => {
		const assignVehicleForPlannedRun = window.angular
			.element(document.body)
			.injector()
			.get('RouteAssigner').assignVehicle;

		executeAction(authStore.crossAppTransport, ModalAction.OPEN_ROUTES_PLANNER_MODAL, {
			optimizationResults: optimizationResult,
			isOpenFromDispatch: false,
			teamId,
			selectedTasks: source.tasks,
			optimizationType: OPTIMIZATION_TYPE.COMBINED,
			optimizationUuid: optimizationResult.request_uuid,
			isManualOptimization: false,
			assignVehicleForPlannedRun,
			onApplyChanges: applyRoutesPlannerChanges,
			disableAssignDriver: true
		});
	};

	return { startOptimization, openRoutesPlanner, onApplyOptimizationResult };
};

export default useOptimizationFunctions;
